import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { styled, Theme } from "@mui/material/styles"
import { FormControlLabel } from "@mui/material"
import Stack from "@mui/material/Stack"
import { FULFILLED } from "constants/index"
import { searchUser } from "utils/componentsUtils"
import { useAppSelector } from "appRedux/hooks"
import { useMakeWishComeTrueMutation } from "appRedux/slices/wishItemApiSlice"
import useFollowers from "hooks/useFollowers"
import Checkbox from "components/atoms/Checkbox"
import UserCard from "components/molecules/UserCard"
import CTA from "components/atoms/CTA"
import BottomSheet from "components/atoms/BottomSheet"
import SearchInput from "components/atoms/SearchInput"

type SelectGeniesProps = {
  show: boolean;
  id: number;
  onOpen: () => void;
  onClose: () => void;
}

export default function SelectGenies(props: SelectGeniesProps) {
  const { show, id, onOpen, onClose } = props

  const [selectedGenieIds, setSelectedGeniesIds] = useState<number[]>([])
  const [localUsers, setLocalUsers] = useState<UserDto[]>([])
  const [searchedValue, setSearchedValue] = useState<string>("")

  const userProfile = useAppSelector((state) => state.user.profile)
  const followers = useAppSelector((state) => state.profileTabs.followers)
  const wishListState = useAppSelector((state) => state.wishList)
  const genies = useAppSelector((state) => (state.wishList.genies && wishListState?.itemInfo?.id) ? state.wishList.genies[wishListState?.itemInfo?.id] : [])

  const [triggerMakeWishComeTrue, makeWishComeTrueResult] = useMakeWishComeTrueMutation()

  useFollowers(userProfile?.id)

  const { t } = useTranslation()

  const usersList = useMemo(() => {
    if (!userProfile) return []
    return [
      {
        id: userProfile.id,
        handle: userProfile.handle,
        name: userProfile.name,
        avatar: userProfile.avatar
      },
      ...followers
    ]
  }, [JSON.stringify(followers), userProfile])

  useEffect(() => {
    const sortedUsers = [...usersList].sort((a, b) => {
      const aSelected = selectedGenieIds.includes(a.id) ? 0 : 1
      const bSelected = selectedGenieIds.includes(b.id) ? 0 : 1
      return aSelected - bSelected
    })
    setLocalUsers(sortedUsers)
  }, [usersList])

  useEffect(() => {
    if (!Array.isArray(genies)) return
    setSelectedGeniesIds(genies.map(genie => genie.id))
  }, [show, JSON.stringify(genies)])

  useEffect(() => {
    if (makeWishComeTrueResult.status === FULFILLED) {
      handleCloseBottomSheet()
    }
  }, [makeWishComeTrueResult])

  function handleSearchUser(event: React.ChangeEvent<HTMLInputElement>) {
    const searchedValue = event.target.value
    const filteredUsers = searchUser(usersList, searchedValue)
    setLocalUsers(filteredUsers)
    setSearchedValue(searchedValue)
  }

  function handleSubmit() {
    if (!selectedGenieIds.length) return
    triggerMakeWishComeTrue({
      id,
      genieIds: selectedGenieIds
    })
  }

  function handleOnUserCheckChange(event: React.SyntheticEvent, userId: number) {
    // @ts-ignore
    const isChecked = event.target.checked
    if (isChecked) {
      setSelectedGeniesIds(prevState => [...prevState, userId])
    } else {
      setSelectedGeniesIds(prevState => prevState.filter(id => id !== userId))
    }
  }

  function handleCloseBottomSheet() {
    setSelectedGeniesIds([])
    if (searchedValue) {
      setSearchedValue("")
      setLocalUsers(usersList)
    }
    onClose()
  }

  return (
    <BottomSheet
      id="select-genies"
      title={t("items.selectGenies")}
      show={show}
      height={"60vh"}
      openBottomSheet={onOpen}
      closeBottomSheet={handleCloseBottomSheet}
    >
      <SelectGeniesContainer>
        <SearchInput
          id={"search-list-input"}
          placeholder={t("general.searchUser")}
          defaultValue={searchedValue}
          onChange={handleSearchUser}
        />
        <Stack gap="8px" overflow="scroll">
          {localUsers.map(user => (
            <UserRow
              key={user.id}
              control={<Checkbox variant="select"/>}
              checked={selectedGenieIds && selectedGenieIds.includes(user.id)}
              isowner={user?.id === userProfile?.id ? 1 : 0}
              label={
                <UserCard
                  id={user?.id}
                  handle={user?.handle}
                  name={user?.name}
                  avatar={user?.avatar}
                  avatarWidth={"46px"}
                  label={userProfile?.id === user?.id ? t("general.myself") : undefined}
                  skipDefaultNavigation={true}
                />
              }
              onChange={(event) => handleOnUserCheckChange(event, user.id)}
            />
          ))}
        </Stack>
      </SelectGeniesContainer>
      <CTA
        variant="contained"
        type="submit"
        loading={makeWishComeTrueResult.isLoading}
        disabled={selectedGenieIds.length === 0 || makeWishComeTrueResult.isLoading}
        onClick={handleSubmit}
      >
        {t("general.submit")}
      </CTA>
    </BottomSheet>
  )
}

const SelectGeniesContainer = styled(Stack)(({ theme }) => ({
  height: "calc(100% - 120px)",
  marginBottom: "8px",
  gap: "8px",

  "& .MuiTextField-root": {
    marginBottom: "0 !important"
  }
}))

type UserRowProps = {
  theme?: Theme;
  isowner: 0 | 1;
}

const UserRow = styled(FormControlLabel)(({ theme, isowner }: UserRowProps) => ({
  width: "100%",
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  margin: 0,
  padding: "0 8px",

  ...(isowner === 1 && {
    "& .avatar": {
      border: `2px solid ${theme?.palette.primary["6"]}`
    },

    "& .user-card p": {
      color: theme?.palette.primary["6"]
    }
  })
}))
